import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  FileText,
  Send,
  Loader2,
  UserPlus,
  X,
  Check,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/utils";
import { adminFetch } from "@/lib/api";
import { useFetch } from "@/hooks/use-fetch";
import { PageLoading } from "@/components/loading";
import { ErrorMessage } from "@/components/error-message";
import { StatusBadge } from "@/components/status-badge";
import { ConfirmModal } from "@/components/confirm-modal";

interface Note {
  id: string;
  content: string;
  authorName: string;
  createdAt: string;
}

interface Contact {
  id: string;
  name: string;
  email: string;
  phone?: string;
  program?: string;
  message: string;
  status: string;
  notes: Note[];
  createdAt: string;
}

interface ContactResponse {
  data: Contact;
}

const statusOptions = [
  { value: "new", label: "Yeni" },
  { value: "read", label: "Okundu" },
  { value: "replied", label: "Yanıtlandı" },
  { value: "completed", label: "Tamamlandı" },
];

export function FormDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [newNote, setNewNote] = useState("");
  const [submittingNote, setSubmittingNote] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [converting, setConverting] = useState(false);
  const [converted, setConverted] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { data, loading, error, refetch } = useFetch<ContactResponse>(
    `/contacts/${id}`
  );

  async function handleStatusChange(newStatus: string) {
    if (!id) return;
    setChangingStatus(true);
    try {
      await adminFetch(`/contacts/${id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      refetch();
    } catch {
      // Error handled by UI
    } finally {
      setChangingStatus(false);
    }
  }

  async function handleAddNote() {
    if (!id || !newNote.trim()) return;
    setSubmittingNote(true);
    try {
      await adminFetch(`/contacts/${id}/notes`, {
        method: "POST",
        body: JSON.stringify({ content: newNote.trim() }),
      });
      setNewNote("");
      refetch();
    } catch {
      // Error handled by UI
    } finally {
      setSubmittingNote(false);
    }
  }

  async function handleDeleteContact() {
    if (!id) return;
    setDeleting(true);
    try {
      await adminFetch(`/contacts/${id}`, { method: "DELETE" });
      navigate("/forms");
    } catch {
      // Hata durumu
      setDeleting(false);
    }
  }

  if (loading) return <PageLoading />;
  if (error) return <ErrorMessage message={error} onRetry={refetch} />;
  if (!data) return null;

  const contact = data.data;

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={() => navigate("/forms")}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Formlara Dön
      </button>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Contact details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Info card */}
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  {contact.name}
                </h2>
                <div className="mt-2 space-y-1.5">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="h-3.5 w-3.5" />
                    <a
                      href={`mailto:${contact.email}`}
                      className="hover:text-accent"
                    >
                      {contact.email}
                    </a>
                  </div>
                  {contact.phone && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="h-3.5 w-3.5" />
                      <a
                        href={`tel:${contact.phone}`}
                        className="hover:text-accent"
                      >
                        {contact.phone}
                      </a>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5" />
                    {formatDate(contact.createdAt)}
                  </div>
                  {contact.program && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <FileText className="h-3.5 w-3.5" />
                      {contact.program}
                    </div>
                  )}
                </div>
              </div>
              <StatusBadge status={contact.status} />
            </div>

            {/* Message */}
            <div className="mt-6 border-t border-border pt-4">
              <h3 className="text-sm font-medium text-foreground">Mesaj</h3>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
                {contact.message}
              </p>
            </div>
          </div>

          {/* Notes */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="text-base font-semibold text-foreground">Notlar</h3>

            {contact.notes && contact.notes.length > 0 ? (
              <div className="mt-4 space-y-3">
                {contact.notes.map((note) => (
                  <div
                    key={note.id}
                    className="rounded-lg border border-border bg-secondary/50 p-4"
                  >
                    <p className="text-sm text-foreground">{note.content}</p>
                    <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-medium">{note.authorName}</span>
                      <span>-</span>
                      <span>{formatDate(note.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground">
                Henüz not eklenmemiş.
              </p>
            )}

            {/* Add note */}
            <div className="mt-4 flex gap-2">
              <input
                type="text"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Not ekle..."
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleAddNote();
                  }
                }}
                className="h-9 flex-1 rounded-lg border border-border bg-secondary px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim() || submittingNote}
                className={cn(
                  "inline-flex h-9 items-center gap-2 rounded-lg bg-accent px-4 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90",
                  (!newNote.trim() || submittingNote) &&
                    "cursor-not-allowed opacity-50"
                )}
              >
                {submittingNote ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5" />
                )}
                Ekle
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar actions */}
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-sm font-medium text-foreground">
              Durum Değiştir
            </h3>
            <div className="mt-3 space-y-2">
              {statusOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleStatusChange(opt.value)}
                  disabled={contact.status === opt.value || changingStatus}
                  className={cn(
                    "flex w-full items-center rounded-lg px-3 py-2 text-sm transition-colors",
                    contact.status === opt.value
                      ? "bg-accent/10 text-accent font-medium"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                    changingStatus && "cursor-not-allowed opacity-50"
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-sm font-medium text-foreground">
              Hızlı İşlemler
            </h3>
            <div className="mt-3 space-y-2">
              <a
                href={`mailto:${contact.email}`}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <Mail className="h-3.5 w-3.5" />
                E-posta Gönder
              </a>
              {contact.phone && (
                <a
                  href={`tel:${contact.phone}`}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  <Phone className="h-3.5 w-3.5" />
                  Ara
                </a>
              )}
              <button
                onClick={() => setShowConvertModal(true)}
                disabled={converted}
                className={cn(
                  "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors",
                  converted
                    ? "text-green-400 cursor-default"
                    : "text-accent hover:bg-accent/10"
                )}
              >
                {converted ? (
                  <>
                    <Check className="h-3.5 w-3.5" />
                    Müşteriye Aktarıldı
                  </>
                ) : (
                  <>
                    <UserPlus className="h-3.5 w-3.5" />
                    Müşteriye Aktar
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Delete */}
          <div className="rounded-xl border border-destructive/30 bg-card p-4">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Formu Sil
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={showDeleteConfirm}
        title="Formu Sil"
        description={`"${contact.name}" tarafından gönderilen formu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`}
        confirmLabel="Sil"
        cancelLabel="İptal"
        variant="danger"
        loading={deleting}
        onConfirm={handleDeleteContact}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* Convert to Client Modal */}
      {showConvertModal && (
        <ConvertToClientModal
          contact={contact}
          converting={converting}
          onClose={() => setShowConvertModal(false)}
          onConvert={async (clientData) => {
            setConverting(true);
            try {
              await adminFetch("/clients", {
                method: "POST",
                body: JSON.stringify(clientData),
              });
              // Mark contact as completed
              await adminFetch(`/contacts/${id}/status`, {
                method: "PATCH",
                body: JSON.stringify({ status: "tamamlandı" }),
              });
              setConverted(true);
              setShowConvertModal(false);
              refetch();
            } catch {
              // Error handled
            } finally {
              setConverting(false);
            }
          }}
        />
      )}
    </div>
  );
}

// ─── Convert to Client Modal ────────────────────

function ConvertToClientModal({
  contact,
  converting,
  onClose,
  onConvert,
}: {
  contact: Contact;
  converting: boolean;
  onClose: () => void;
  onConvert: (data: Record<string, string>) => void;
}) {
  const [form, setForm] = useState({
    company_name: "",
    contact_name: contact.name,
    contact_email: contact.email,
    contact_phone: contact.phone || "",
    sector: "",
    notes: `İletişim formundan aktarıldı. Program: ${contact.program || "-"}`,
    client_type: "potential",
  });

  const inputClass =
    "h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent";

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.company_name.trim() || !form.contact_name.trim()) return;
    onConvert(form);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border border-border/60 bg-card p-6 shadow-2xl ring-1 ring-white/5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">Müşteriye Aktar</h2>
          <button onClick={onClose} className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Firma Adı *</label>
              <input type="text" required value={form.company_name} onChange={(e) => setForm(prev => ({ ...prev, company_name: e.target.value }))} className={inputClass} placeholder="Firma adı" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">İlgili Kişi *</label>
              <input type="text" required value={form.contact_name} onChange={(e) => setForm(prev => ({ ...prev, contact_name: e.target.value }))} className={inputClass} placeholder="Ad Soyad" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">E-posta</label>
              <input type="email" value={form.contact_email} onChange={(e) => setForm(prev => ({ ...prev, contact_email: e.target.value }))} className={inputClass} placeholder="ornek@firma.com" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Telefon</label>
              <input type="tel" value={form.contact_phone} onChange={(e) => setForm(prev => ({ ...prev, contact_phone: e.target.value }))} className={inputClass} placeholder="Telefon" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Sektör</label>
              <input type="text" value={form.sector} onChange={(e) => setForm(prev => ({ ...prev, sector: e.target.value }))} className={inputClass} placeholder="Sektör" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Müşteri Türü</label>
              <select value={form.client_type} onChange={(e) => setForm(prev => ({ ...prev, client_type: e.target.value }))} className={inputClass}>
                <option value="potential">Potansiyel</option>
                <option value="active">Aktif</option>
              </select>
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Notlar</label>
            <textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} rows={3} className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent" />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
              İptal
            </button>
            <button type="submit" disabled={converting} className={cn("inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90", converting && "cursor-not-allowed opacity-50")}>
              {converting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Müşteri Oluştur
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
